/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Sujith Dachepally
 * Created: 06/10/23
 */

import React, { useEffect, useState } from 'react';
import { Badge, Box, Fade, Heading, HStack, Text } from '@chakra-ui/react';
import { useFormik } from 'formik';
import { FormDateInput } from '@/components/FormInputField/FormDateInput';
import { usePutRequest } from '@/utils/useStatefulRequests';
import { SimpleAlert } from '@/components/SimpleAlert/SimpleAlert';
import { LoadingSpinner } from '@/components';
import { Assignment } from '@/wpt-lib/interfaces/Assignment';
import { UpdateWorkplanMeetingDatesDto, Workplan, WorkplanVersion } from '@/wpt-lib/interfaces/Workplan';
import { CalendarDownload } from '@/components/FormInputField/CalendarDownload';
import useNavigateOptions from '@/components/contexts/NaviateContext/useNavigateOptions';

interface MeetingDatesTileProps {
    currentAssignment: Assignment;
    workplan: Workplan | undefined;
    workplanVersion: WorkplanVersion | undefined;
    loading: boolean;
    error: boolean;
    baseApiUrl: string;
    isReadonly: boolean;
    version?: number;
}
export const MeetingDatesTile = ({
    currentAssignment,
    workplan,
    workplanVersion,
    loading,
    error,
    baseApiUrl,
    isReadonly,
}: MeetingDatesTileProps) => {
    const { error: submitWorkplanMeetingDatesError, makeRequest: submitWorkplanMeetingDates } = usePutRequest<
        unknown,
        UpdateWorkplanMeetingDatesDto
    >();

    const { supervisor } = useNavigateOptions();
    const [saved, setSaved] = useState<number | undefined>(undefined);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            planningMeetingDate: workplanVersion?.planningMeetingDate ?? '',
            progressReviewMeetingDate: workplanVersion?.progressReviewMeetingDate ?? '',
            endOfYearMeetingDate: workplanVersion?.endOfYearMeetingDate ?? '',
        },
        onSubmit: async (values) => {
            try {
                await submitWorkplanMeetingDates(
                    baseApiUrl,
                    {
                        meetingDates: {
                            planningMeetingDate: values.planningMeetingDate !== '' ? values.planningMeetingDate : null,
                            progressReviewMeetingDate:
                                values.progressReviewMeetingDate !== '' ? values.progressReviewMeetingDate : null,
                            endOfYearMeetingDate:
                                values.endOfYearMeetingDate !== '' ? values.endOfYearMeetingDate : null,
                        },
                    },
                    {
                        params: {
                            assignment: currentAssignment.id,
                        },
                    },
                );
                setSaved(Date.now());
                setTimeout(() => setSaved((saved) => (saved && Date.now() - saved > 4000 ? undefined : saved)), 5000);
            } catch (e) {
                // handled by hook
            }
        },
    });

    useEffect(() => {
        if (
            !isReadonly &&
            (formik.values.planningMeetingDate !== (workplanVersion?.planningMeetingDate ?? '') ||
                formik.values.progressReviewMeetingDate !== (workplanVersion?.progressReviewMeetingDate ?? '') ||
                formik.values.endOfYearMeetingDate !== (workplanVersion?.endOfYearMeetingDate ?? ''))
        ) {
            formik.submitForm();
        }
    }, [
        formik.values.planningMeetingDate,
        formik.values.progressReviewMeetingDate,
        formik.values.endOfYearMeetingDate,
        isReadonly,
    ]);

    if (loading) {
        return <LoadingSpinner />;
    }

    if (error) {
        return (
            <SimpleAlert variant={'left-accent'}>
                <Text>Cannot get meeting Dates, please refresh the page and try again</Text>
            </SimpleAlert>
        );
    }

    return (
        <Box my={2}>
            <HStack sx={{ alignItems: 'flex-end' }}>
                <Heading as="h3" size={'sm'}>
                    Proposed Meeting Dates
                </Heading>
                <Fade in={!!saved}>
                    <Box>
                        <Badge colorScheme={'green'} sx={{ textTransform: 'none', fontWeight: 'normal' }}>
                            Saved
                        </Badge>
                    </Box>
                </Fade>
            </HStack>
            <HStack spacing="10px">
                <FormDateInput
                    handleChange={formik.handleChange}
                    value={formik.values.planningMeetingDate}
                    fieldName="planningMeetingDate"
                    isReadonly={isReadonly}
                    label="Planning"
                    onFocus={() => setSaved(undefined)}
                />
                {formik.values.planningMeetingDate && (
                    <CalendarDownload
                        dateValue={formik.values.planningMeetingDate}
                        title={`${workplan?.year} Workplan Planning Review (Work Planning Tool)`}
                        description={`${
                            workplan?.year
                        } workplan planning review meeting. Please adjust meeting time and send to ${
                            supervisor ? 'workplan author.' : 'your supervisor.'
                        }`}
                    />
                )}
            </HStack>
            <HStack spacing="10px">
                <FormDateInput
                    handleChange={formik.handleChange}
                    value={formik.values.progressReviewMeetingDate}
                    fieldName="progressReviewMeetingDate"
                    isReadonly={isReadonly}
                    label="Progress Review"
                    onFocus={() => setSaved(undefined)}
                />
                {formik.values.progressReviewMeetingDate && (
                    <CalendarDownload
                        dateValue={formik.values.progressReviewMeetingDate}
                        title={`${workplan?.year} Workplan Progress Review (Work Planning Tool)`}
                        description={`${
                            workplan?.year
                        } workplan progress review meeting. Please adjust meeting time and send to ${
                            supervisor ? 'workplan author.' : 'your supervisor.'
                        }`}
                    />
                )}
            </HStack>
            <HStack spacing="10px" width="400px">
                <FormDateInput
                    handleChange={formik.handleChange}
                    value={formik.values.endOfYearMeetingDate}
                    fieldName="endOfYearMeetingDate"
                    isReadonly={isReadonly}
                    label="End Year"
                    onFocus={() => setSaved(undefined)}
                />
                {formik.values.endOfYearMeetingDate && (
                    <CalendarDownload
                        dateValue={formik.values.endOfYearMeetingDate}
                        title={`${workplan?.year} Workplan End Year Review (Work Planning Tool)`}
                        description={`${
                            workplan?.year
                        } workplan end year review meeting. Please adjust meeting time and send to ${
                            supervisor ? 'workplan author.' : 'your supervisor.'
                        }`}
                    />
                )}
            </HStack>
            {!isReadonly && !!submitWorkplanMeetingDatesError && (
                <SimpleAlert variant={'left-accent'}>
                    <Text>Could not save meeting dates, please refresh the page and try again</Text>
                </SimpleAlert>
            )}
        </Box>
    );
};
